import type * as z from "zod";
import {
  CustomAnalyticsQueryComparisonOperatorSchema,
  CustomAnalyticsQueryScalarValueSchema,
} from "../shared/custom-analytics-query-primitives";
import { AnalyticsValueType } from "./analytics-query-constants";

export type AnalyticsComparisonOperator = z.infer<
  typeof CustomAnalyticsQueryComparisonOperatorSchema
>;

export type AnalyticsAllowedValue = z.infer<
  typeof CustomAnalyticsQueryScalarValueSchema
>;

export const AnalyticsComparisonOpsWithValue = [
  "eq",
  "neq",
  "gt",
  "gte",
  "lt",
  "lte",
  "like",
  "ilike",
  "in",
  "not_in",
  "is_distinct_from",
  "array_contains",
] as const satisfies readonly AnalyticsComparisonOperator[];

export type AnalyticsComparisonOpWithValue =
  (typeof AnalyticsComparisonOpsWithValue)[number];

export const AnalyticsComparisonOpsWithoutValue = [
  "is_null",
  "is_not_null",
] as const satisfies readonly AnalyticsComparisonOperator[];

export type AnalyticsComparisonOpWithoutValue =
  (typeof AnalyticsComparisonOpsWithoutValue)[number];

export const AnalyticsComparisonOps = [
  ...AnalyticsComparisonOpsWithValue,
  ...AnalyticsComparisonOpsWithoutValue,
] as const satisfies readonly AnalyticsComparisonOperator[];

type ClassifiedAnalyticsComparisonOperator =
  | AnalyticsComparisonOpWithValue
  | AnalyticsComparisonOpWithoutValue;
type UnclassifiedAnalyticsComparisonOperator = Exclude<
  AnalyticsComparisonOperator,
  ClassifiedAnalyticsComparisonOperator
>;
type DuplicateAnalyticsComparisonOperatorClassification = Extract<
  AnalyticsComparisonOpWithValue,
  AnalyticsComparisonOpWithoutValue
>;
type AssertNever<T extends never> = T;
export type AnalyticsComparisonOperatorClassificationCheck = AssertNever<
  | UnclassifiedAnalyticsComparisonOperator
  | DuplicateAnalyticsComparisonOperatorClassification
>;

const AnalyticsExactFilterOpsWithValue = [
  "eq",
  "neq",
  "in",
  "not_in",
  "is_distinct_from",
] as const satisfies readonly AnalyticsComparisonOperator[];

export const AnalyticsExactFilterOps = [
  ...AnalyticsExactFilterOpsWithValue,
  ...AnalyticsComparisonOpsWithoutValue,
] as const satisfies readonly AnalyticsComparisonOperator[];

export const AnalyticsOrderedFilterOps = [
  ...AnalyticsExactFilterOpsWithValue,
  "gt",
  "gte",
  "lt",
  "lte",
  ...AnalyticsComparisonOpsWithoutValue,
] as const satisfies readonly AnalyticsComparisonOperator[];

export const AnalyticsTextFilterOps = [
  ...AnalyticsExactFilterOpsWithValue,
  "like",
  "ilike",
  ...AnalyticsComparisonOpsWithoutValue,
] as const satisfies readonly AnalyticsComparisonOperator[];

export const AnalyticsArrayFilterOps = [
  "array_contains",
  ...AnalyticsComparisonOpsWithoutValue,
] as const satisfies readonly AnalyticsComparisonOperator[];

export function getAnalyticsFilterOpsForValueType({
  allowedValues,
  valueType,
}: {
  readonly allowedValues?: readonly AnalyticsAllowedValue[];
  readonly valueType: AnalyticsValueType;
}): readonly AnalyticsComparisonOperator[] {
  if (valueType === AnalyticsValueType.Array) {
    return AnalyticsArrayFilterOps;
  }

  if (allowedValues && allowedValues.length > 0) {
    return AnalyticsExactFilterOps;
  }

  switch (valueType) {
    case AnalyticsValueType.Date:
    case AnalyticsValueType.Datetime:
    case AnalyticsValueType.Money:
    case AnalyticsValueType.Number:
      return AnalyticsOrderedFilterOps;
    case AnalyticsValueType.String:
      return AnalyticsTextFilterOps;
    case AnalyticsValueType.Boolean:
    case AnalyticsValueType.Id:
      return AnalyticsExactFilterOps;
    default: {
      const exhaustive: never = valueType;
      return exhaustive;
    }
  }
}

export function isAnalyticsFilterOpAllowed({
  allowedValues,
  op,
  valueType,
}: {
  readonly allowedValues?: readonly AnalyticsAllowedValue[];
  readonly op: AnalyticsComparisonOperator;
  readonly valueType: AnalyticsValueType;
}) {
  return getAnalyticsFilterOpsForValueType({
    allowedValues,
    valueType,
  }).includes(op);
}
